import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	InputAdornment,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { AiModelPrice } from "Infrastructure/Api/ApiCustom";
import { useEffect, useState } from "react";

type ModelPriceValues = Omit<AiModelPrice, "aiModel">;

const price = (input: number, cached: number, output: number): ModelPriceValues => ({
	inputPricePerMillion: input,
	cachedInputPricePerMillion: cached,
	outputPricePerMillion: output,
});

const DEFAULT_MODEL_PRICE = price(2.5, 1.25, 10);

const KNOWN_MODEL_PRICES: Record<string, ModelPriceValues> = {
	"gpt-5.4-mini": price(0.75, 0.075, 4.5),
	"gpt-5.4": price(2.5, 0.25, 15),
	"gpt-5-mini": price(0.25, 0.025, 2),
	"gpt-5-nano": price(0.05, 0.005, 0.4),
	"gpt-5": price(1.25, 0.125, 10),
	"gpt-4.1-mini": price(0.4, 0.1, 1.6),
	"gpt-4.1": price(2, 0.5, 8),
	"gpt-4o-mini": price(0.15, 0.075, 0.6),
	"gpt-4o": price(2.5, 1.25, 10),
	"text-embedding-3-large": price(0.13, 0, 0),
	"text-embedding-3-small": price(0.02, 0, 0),
};

const findKnownModel = (model: string) => {
	const normalized = model.toLowerCase();
	return Object.keys(KNOWN_MODEL_PRICES).find(
		(known) =>
			normalized === known ||
			new RegExp(`^${known.replace(/\./g, "\\.")}-\\d`).test(normalized),
	);
};

const getDefaultPrice = (model: string): ModelPriceValues => {
	const knownModel = findKnownModel(model);
	return knownModel ? KNOWN_MODEL_PRICES[knownModel] : DEFAULT_MODEL_PRICE;
};

type PriceField = keyof ModelPriceValues;

const PRICE_FIELDS: { field: PriceField; label: string }[] = [
	{ field: "inputPricePerMillion", label: "Input / 1M" },
	{ field: "cachedInputPricePerMillion", label: "Cached input / 1M" },
	{ field: "outputPricePerMillion", label: "Output / 1M" },
];

type Props = {
	open: boolean;
	models: string[];
	prices: AiModelPrice[];
	isExporting: boolean;
	onClose: () => void;
	onConfirm: (prices: AiModelPrice[]) => void;
};

const toModelKey = (model: string | null) => model ?? "";

export const AiModelPricesDialog: React.FunctionComponent<Props> = ({
	open,
	models,
	prices,
	isExporting,
	onClose,
	onConfirm,
}) => {
	const [values, setValues] = useState<Record<string, Record<PriceField, string>>>({});

	useEffect(() => {
		if (!open) {
			return;
		}

		const initialValues: Record<string, Record<PriceField, string>> = {};
		for (const model of models) {
			const modelPrice =
				prices.find((x) => toModelKey(x.aiModel) === model) ?? getDefaultPrice(model);
			initialValues[model] = {
				inputPricePerMillion: String(modelPrice.inputPricePerMillion),
				cachedInputPricePerMillion: String(modelPrice.cachedInputPricePerMillion),
				outputPricePerMillion: String(modelPrice.outputPricePerMillion),
			};
		}
		setValues(initialValues);
	}, [open, models, prices]);

	const parse = (value: string | undefined) => Number((value ?? "").replace(",", "."));

	const isValid = models.every((model) =>
		PRICE_FIELDS.every(({ field }) => {
			const value = parse(values[model]?.[field]);
			return Number.isFinite(value) && value >= 0;
		}),
	);

	const handleChange = (model: string, field: PriceField, value: string) =>
		setValues((current) => ({
			...current,
			[model]: { ...current[model], [field]: value },
		}));

	const handleConfirm = () =>
		onConfirm(
			models.map((model) => ({
				aiModel: model === "" ? null : model,
				inputPricePerMillion: parse(values[model]?.inputPricePerMillion),
				cachedInputPricePerMillion: parse(values[model]?.cachedInputPricePerMillion),
				outputPricePerMillion: parse(values[model]?.outputPricePerMillion),
			})),
		);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>Ceník modelů pro export</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary" mb={2}>
					Ceny jsou v USD za 1 milion tokenů a nikam se neukládají. Výchozí hodnoty odpovídají ceníku
					OpenAI (standard tier, říjen 2026), neznámé modely mají předvyplněné ceny gpt-4o. Cena volání = (input −
					cached) × input cena + cached × cached cena + output × output cena.
				</Typography>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
							{PRICE_FIELDS.map(({ field, label }) => (
								<TableCell key={field} sx={{ fontWeight: 600 }}>
									{label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{models.map((model) => (
							<TableRow key={model}>
								<TableCell sx={{ whiteSpace: "nowrap" }}>
									{model === "" ? (
										<Typography variant="body2" color="text.secondary">
											(neuvedeno)
										</Typography>
									) : (
										model
									)}
									{!findKnownModel(model) && (
										<Typography variant="caption" color="warning.main" display="block">
											Neznámý model – zkontrolujte cenu
										</Typography>
									)}
								</TableCell>
								{PRICE_FIELDS.map(({ field }) => {
									const value = values[model]?.[field] ?? "";
									const parsed = parse(value);
									return (
										<TableCell key={field}>
											<TextField
												size="small"
												value={value}
												onChange={(e) => handleChange(model, field, e.target.value)}
												error={!Number.isFinite(parsed) || parsed < 0}
												inputProps={{ inputMode: "decimal" }}
												InputProps={{
													startAdornment: <InputAdornment position="start">$</InputAdornment>,
												}}
												sx={{ width: 140 }}
											/>
										</TableCell>
									);
								})}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Zrušit</Button>
				<Button variant="contained" disabled={!isValid || isExporting} onClick={handleConfirm}>
					Exportovat
				</Button>
			</DialogActions>
		</Dialog>
	);
};
